import type * as React from "react";
import { memo, type ReactNode, useEffect } from "react";
import { type PdpPageProductData, UnitTagType } from "../../models/PdpPageProductData";
import { TechnicalDetailsTable } from "../DataTables/TechnicalDetailsTable";
import { SpecificationsCard } from "../SpecificationsCard/SpecificationsCard";
import SimpleExternalContent from "../../../Configurators/Linear/components/SimpleExternalContent";
import { Row } from "../../../common/components/Layout";
import { type FootnoteDTO, ImageFormat } from "../../../dto/ProductDataDTO";

import { DST_PRODUCT_CODE } from "../../../utils/constants";
import { Carousel } from "../../../common/components/carousel/Carousel";
import CenterContents from "../../../common/components/CenterContents";
import useMediaQuery from "../../../common/hooks/useMediaQuery";
import { ReduxImperialToggle } from "../../../feature/UnitMode/UnitModeToggle";
import { useUnitMode } from "../../../feature/UnitMode/useUnitMode";
import { useDispatch } from "react-redux";
import { UnitMode } from "../../../feature/UnitMode/UnitMode";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "../../../utils/screenSizes";
import { useTranslation } from "react-i18next";
import { QualtricsContainer } from "../ProductSummary/common/QualtricsContainer";
import { Alert } from "~/common/m3/Alert/Alert";
import { changeUnitModeThunk } from "~/feature/UserPreferences/userPreferencesSlice";

interface CalculationTabProps {
  product: PdpPageProductData;
}

// TODO: Revisit the information shown here. commentsFootnotes are shown in both conditions.

const ProductInformationTab: React.FC<CalculationTabProps> = ({ product }) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);
  const dispatch = useDispatch();

  const unitMode = useUnitMode();

  const showCommentFootnotes = product.code === DST_PRODUCT_CODE;
  const showRegularTechDetails = !showCommentFootnotes;

  const renderMessage = () => {
    let message;
    if (product.unitTag === UnitTagType.Metric && unitMode === UnitMode.Imperial) {
      message = t("productDetailsPage.trueMetric");
    } else if (product.unitTag === UnitTagType.Imperial && unitMode === UnitMode.Metric) {
      message = t("productDetailsPage.trueImperial");
    } else if (product.unitTag === UnitTagType.Hybrid) {
      message = t("productDetailsPage.hybrid");
    } else {
      message = undefined;
    }

    if (!message) {
      return null;
    }

    return (
      <Alert severity={"info"} className={"scha__pdp__unitMode-msg"}>
        {message}
      </Alert>
    );
  };

  useEffect(() => {
    dispatch(changeUnitModeThunk(product.unitTag === UnitTagType.Imperial ? UnitMode.Imperial : UnitMode.Metric));
  }, []);

  return (
    <div className={isDesktop ? undefined : "w-100"}>
      <Row className={"scha__pdp__info-row"}>
        <div className={"scha__pdp__techimages-col col-md-3"}>
          {isDesktop &&
            product.techImages
              .filter((imageData) => {
                return imageData.format === ImageFormat.superZoom;
              })
              .map((imageData, index) => (
                <div key={index} className={"mb-30"}>
                  <img
                    className={"mb-2"}
                    src={imageData.url}
                    alt={product.designation}
                    title={product.designation}
                    data-test-id={"technical-image"}
                  />
                  <div className="scha__pdp__footnotes">
                    {imageData.commentsFootnotes &&
                      imageData.commentsFootnotes.map((footnote) => (
                        <div key={footnote.currentFootnotesURL} className="scha__pdp__footnotes__single-footnote">
                          <Footnote footnote={footnote} />
                        </div>
                      ))}
                  </div>
                </div>
              ))}

          {!isDesktop && (
            <Carousel
              data={product.techImages.filter((imageData) => {
                return imageData.format === ImageFormat.superZoom;
              })}
              pageItemsCount={1}
              renderSlide={(props) => {
                const imageData = props.data[0];
                return (
                  <div>
                    <MobilePdpImageContainer>
                      <img
                        src={imageData.url}
                        alt={product.designation}
                        title={product.designation}
                        data-test-id={"technical-image"}
                      />
                    </MobilePdpImageContainer>

                    <div className="scha__pdp__footnotes">
                      {imageData.commentsFootnotes &&
                        imageData.commentsFootnotes.map((footnote) => (
                          <div key={footnote.currentFootnotesURL} className="scha__pdp__footnotes__single-footnote">
                            <Footnote footnote={footnote} />
                          </div>
                        ))}
                    </div>
                  </div>
                );
              }}
            />
          )}
        </div>
        {showCommentFootnotes && (
          <div className={"scha__pdp__techdetails-col col-md-9"}>
            {product.commentsFootnotes.map((footnote) => (
              <Footnote key={footnote.currentFootnotesURL} footnote={footnote} />
            ))}
          </div>
        )}
        {showRegularTechDetails && (
          <>
            <div className={"scha__pdp__techdetails-col col-md-5"}>
              <div className="scha__pdp__techdetails-col__table-list" data-test-id={"whole-technical-details-table"}>
                {product?.technicalDetails.length > 0 && <ReduxImperialToggle page="PDP" />}
                {product.technicalDetails.map((entry, index) => (
                  <TechnicalDetailsTable key={index} product={product} data={entry} />
                ))}
                {renderMessage()}
              </div>
              <div className="scha__pdp__footnotes">
                {product.footnotes.length > 0 && (
                  <h4 className="scha__pdp__footnotes__title">{t("pdp.footnotes.title")}</h4>
                )}
                {product.footnotes.map((footnote, index) => (
                  <div key={footnote.currentFootnotesURL} className="scha__pdp__footnotes__single-footnote">
                    <sup>{index + 1}</sup>
                    <Footnote key={footnote.currentFootnotesURL} footnote={footnote} />
                  </div>
                ))}
              </div>
              <div className="scha__pdp__html-description">
                {product.commentsFootnotes.map((footnote) => (
                  <Footnote key={footnote.currentFootnotesURL} footnote={footnote} />
                ))}
              </div>
            </div>

            <div className={"scha__pdp__specifications-col col-md-4"}>
              <SpecificationsCard product={product} />
              <QualtricsContainer id={"pdpQuickFeedback"} />
            </div>
          </>
        )}
      </Row>
    </div>
  );
};

export default memo(ProductInformationTab);

const Footnote = ({ footnote }: { footnote: FootnoteDTO }) => (
  <div className="scha__lc__external-content--shaft">
    <SimpleExternalContent
      content={footnote.currentFootnotesURL}
      error={<SimpleExternalContent content={footnote.fallbackFootnotesURL} />}
    />
  </div>
);

const MobilePdpImageContainer = ({ children }: { children?: ReactNode }) => (
  <CenterContents className="scha__pdp-mobile__tech-image-container">{children}</CenterContents>
);
